import { legacyWindow } from '../strangler'

// Stable DOM addressing (step 4). Every positionally-addressed element
// carries data-ui="<family>:<name>" (stamped by test/tools/annotate-dom.js);
// this registry resolves them by name and — critically — verifies at boot
// that registry order is element-identical to the positional order the 669
// legacy getElementsByClassName("...")[n] sites still rely on. Markup drift
// that would silently misrender becomes a loud boot failure.

interface Family {
  names: string[]
  elements: Element[]
  byName: Map<string, Element>
}

const families = new Map<string, Family>()

export function buildRegistry(doc: Document): void {
  families.clear()
  const annotated = doc.querySelectorAll('[data-ui]')
  for (const el of Array.from(annotated)) {
    const tokens = (el.getAttribute('data-ui') || '').split(/\s+/).filter(Boolean)
    for (const token of tokens) {
      const sep = token.indexOf(':')
      if (sep === -1) throw new Error(`malformed data-ui token "${token}"`)
      const family = token.slice(0, sep)
      const name = token.slice(sep + 1)
      let f = families.get(family)
      if (!f) {
        f = { names: [], elements: [], byName: new Map() }
        families.set(family, f)
      }
      if (f.byName.has(name)) throw new Error(`duplicate data-ui name ${family}:${name}`)
      f.names.push(name)
      f.elements.push(el)
      f.byName.set(name, el)
    }
  }
  verifyPositionalParity(doc)
}

/**
 * The boot alarm: for every family, registry order must be identity-equal to
 * the live class collection the legacy positional sites read. (window-body
 * intentionally has 48 elements vs the 47-box loop — see the registry tests.)
 */
function verifyPositionalParity(doc: Document): void {
  for (const [family, f] of families) {
    const positional = doc.getElementsByClassName(family)
    if (positional.length !== f.elements.length) {
      throw new Error(`ui registry: family "${family}" has ${f.elements.length} annotated elements but ${positional.length} positional ones — markup drifted`)
    }
    for (let i = 0; i < f.elements.length; i++) {
      if (positional[i] !== f.elements[i]) {
        throw new Error(`ui registry: family "${family}" order mismatch at index ${i} (${f.names[i]}) — markup drifted`)
      }
    }
  }
}

/** Resolve one element: uiElement('box:dragon'). Unknown name = bug, throws. */
export function uiElement(token: string): Element {
  const sep = token.indexOf(':')
  if (sep === -1) throw new Error(`uiElement: token "${token}" must be "family:name"`)
  const f = families.get(token.slice(0, sep))
  const el = f?.byName.get(token.slice(sep + 1))
  if (!el) throw new Error(`uiElement: unknown ${token}`)
  return el
}

/** All elements of a family, in document order. */
export function uiFamily(family: string): Element[] {
  const f = families.get(family)
  if (!f) throw new Error(`uiFamily: unknown family "${family}"`)
  return [...f.elements]
}

/** The stable names of a family, in document order. */
export function uiNames(family: string): string[] {
  const f = families.get(family)
  if (!f) throw new Error(`uiNames: unknown family "${family}"`)
  return [...f.names]
}

export function uiFamilyList(): string[] {
  return [...families.keys()]
}

/**
 * Repoint the legacy cachedBoxes cache (render.js top-level, read 96x per
 * render tick) at the registry's box array — same 47 elements in the same
 * order (parity-verified above), so render() behavior is byte-identical but
 * the cache is now registry-fed.
 */
export function adoptCachedBoxes(): void {
  const boxes = uiFamily('box')
  if (boxes.length !== 47) throw new Error(`expected 47 boxes, registry has ${boxes.length}`)
  const legacy = legacyWindow.cachedBoxes as unknown[]
  if (!Array.isArray(legacy) || legacy.length !== 47) {
    throw new Error('cachedBoxes missing or wrong size at adoption time')
  }
  for (let i = 0; i < 47; i++) {
    if (legacy[i] !== boxes[i]) throw new Error(`cachedBoxes[${i}] !== registry box "${uiNames('box')[i]}"`)
  }
  legacyWindow.cachedBoxes = boxes
}
